import { useMutation } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { RRule, Options as RRuleOptions } from 'rrule';
import { Mutations } from '@shared/api';
import { getEntityTitle } from '@shared/data/entity';
import { MAX_COUNT } from '@shared/scenes/entity/components/fields/DateEditField/utils';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isDateComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { getTimezone } from '@shared/util/timezone';
import {
  BatchFieldOverrides,
  getFinalFieldOverrides,
  useBatchFieldOverrides,
} from '../useBatchFieldOverrides';
import { BatchProvider } from './useBatchContext';

function noop(): void {}

// TODO can we fetch batch here?
export function BatchEditContextProvider({ children }: { children: ReactNode }) {
  const [rruleOptions, setRruleOptions] = useState<Partial<RRuleOptions> | undefined>();
  const contextValue = useMemo(
    () => ({
      shouldBatchCreate: false,
      canBatchCreate: false,
      recurrenceType: undefined,
      setRecurrenceType: noop,
      offset: null,
      setOffset: noop,
      userIsEditing: false,
      setUserIsEditing: noop,
      rruleOptions,
      setRruleOptions,
      minimumInSeries: undefined,
      setMinimumInSeries: noop,
      tagFilter: undefined,
      setTagFilter: noop,
      batchCreate: () => {
        return null;
      },
    }),
    [rruleOptions],
  );
  return <BatchProvider value={contextValue}>{children}</BatchProvider>;
}

export function ExternalBatchContextProvider({ children }: { children: ReactNode }) {
  const [rruleOptions, setRruleOptions] = useState<Partial<RRuleOptions> | undefined>();
  const contextValue = useMemo(
    () => ({
      shouldBatchCreate: false,
      canBatchCreate: false,
      recurrenceType: undefined,
      setRecurrenceType: noop,
      offset: null,
      setOffset: noop,
      userIsEditing: false,
      setUserIsEditing: noop,
      rruleOptions,
      setRruleOptions,
      minimumInSeries: undefined,
      setMinimumInSeries: noop,
      tagFilter: undefined,
      setTagFilter: noop,
      batchCreate: () => {
        return null;
      },
    }),
    [rruleOptions],
  );
  return <BatchProvider value={contextValue}>{children}</BatchProvider>;
}

export function BatchCreateContextProvider({
  workflowTemplateId,
  children,
}: {
  workflowTemplateId?: string | null;
  children: ReactNode;
}) {
  const [recurrenceType, setRecurrenceType] = useState<API.RecurrenceType | undefined>(
    'RRULE_SCHEDULE',
  );
  const [offset, setOffset] = useState<API.CreateRecurrenceOffset | null>(null);
  const [rruleOptions, setRruleOptions] = useState<Partial<RRuleOptions> | undefined>();
  const [minimumInSeries, setMinimumInSeries] = useState<number | undefined>(MAX_COUNT);
  const [tagFilter, setTagFilter] = useState<API.FilterByComponentFragment>();
  const [userIsEditing, setUserIsEditing] = useState(false);
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const [createRecurringEntities] = useMutation<
    API.CreateRecurringEntitiesMutation,
    API.CreateRecurringEntitiesMutationVariables
  >(Mutations.entity.createRecurringEntities);

  // When recurrence type changes, clear the other type's fields
  useEffect(() => {
    if (recurrenceType === 'OFFSET') {
      // Clear RRULE_SCHEDULE fields
      setRruleOptions(undefined);
      setMinimumInSeries(undefined);
      setTagFilter(undefined);
    } else if (recurrenceType === 'RRULE_SCHEDULE') {
      // Clear OFFSET fields
      setOffset(null);
    }
  }, [recurrenceType]);

  const dateComponentId = useMemo(() => {
    return (
      workflowTemplate?.components?.find(
        component => isDateComponent(component) && component.allowEntityBatchCreation,
      )?.id ?? ''
    );
  }, [workflowTemplate?.components]);

  const batchFieldOverrides = useBatchFieldOverrides(workflowTemplate?.components);

  // reset autoextend options when rrule is cleared
  useEffect(() => {
    if (isNil(rruleOptions)) {
      setMinimumInSeries(MAX_COUNT);
      setTagFilter(undefined);
    }
  }, [rruleOptions, setMinimumInSeries, setTagFilter]);

  const batchCreate = useCallback(
    (entity: API.CreateWorkflowEntity) => {
      if (isNil(recurrenceType)) {
        return null;
      }

      const name = getEntityTitle(entity, workflowTemplate) ?? '';
      const now = moment().format('MMM D, YYYY, h:mm a');
      const components = workflowTemplate?.components;
      const fieldsToRemove = [
        'createdAt',
        'updatedAt',
        'sequenceNumber',
        'entityBatch',
        'createdBy',
      ];

      // Remove low-code added fields and source card-specific fields
      for (let fieldKey in entity.fields) {
        const fieldHasComponent = components?.some(c => c.id === fieldKey);
        if (!fieldHasComponent) {
          delete entity.fields[fieldKey];
        }
        if (fieldsToRemove.includes(fieldKey)) {
          delete entity.fields[fieldKey];
        }
      }

      const fieldOverrides = getFinalFieldOverrides({
        fieldOverrides: batchFieldOverrides,
        entity,
        components,
      });

      const seriesFilter = getTagFilter({ tagFilter, entity, fieldOverrides });

      if (
        !validateSeriesFilter({
          seriesFilter,
          entity,
          fieldOverrides,
        })
      ) {
        return null;
      }

      const extend = !isNil(seriesFilter)
        ? {
            autoExtend: {
              seriesFilter: [seriesFilter],
              minimumInSeries: recurrenceType === 'OFFSET' ? 1 : minimumInSeries,
            },
          }
        : undefined;

      const isOffset = recurrenceType === 'OFFSET';
      const rrule = isOffset ? undefined : new RRule(rruleOptions);
      const scheduleName = isOffset
        ? `${name} (created ${now})`
        : `${name}, ${rrule?.toText()} (created ${now})`;

      const offsetWithTimezone = !isNil(offset) ? { ...offset, timezone: getTimezone() } : offset;

      const input: API.CreateRecurringEntities = {
        schedule: {
          type: recurrenceType,
          name: scheduleName,
          dateComponentId,
          ...(!isNil(extend) ? { extend } : {}),
          ...(isOffset ? { offset: offsetWithTimezone } : { rrule: rrule?.toString() }),
        },
        entity,
        ...fieldOverrides,
      };

      return createRecurringEntities({
        variables: {
          input,
        },
      });
    },
    [
      createRecurringEntities,
      dateComponentId,
      recurrenceType,
      offset,
      rruleOptions,
      workflowTemplate,
      batchFieldOverrides,
      minimumInSeries,
      tagFilter,
    ],
  );

  const shouldBatchCreate = useMemo(() => {
    if (recurrenceType === 'OFFSET') {
      return !isNil(offset?.unit) && !isNil(offset?.value) && !isNil(offset?.initialDate);
    }
    if (recurrenceType === 'RRULE_SCHEDULE') {
      return !isNil(rruleOptions?.freq);
    }
    return false;
  }, [recurrenceType, offset, rruleOptions?.freq]);

  const contextValue = useMemo(
    () => ({
      shouldBatchCreate,
      canBatchCreate: true,
      recurrenceType,
      setRecurrenceType,
      offset,
      setOffset,
      rruleOptions,
      setRruleOptions,
      batchCreate,
      userIsEditing,
      setUserIsEditing,
      minimumInSeries,
      setMinimumInSeries,
      tagFilter,
      setTagFilter,
    }),
    [
      shouldBatchCreate,
      batchCreate,
      recurrenceType,
      setRecurrenceType,
      offset,
      setOffset,
      rruleOptions,
      setRruleOptions,
      userIsEditing,
      setUserIsEditing,
      minimumInSeries,
      setMinimumInSeries,
      tagFilter,
      setTagFilter,
    ],
  );
  return <BatchProvider value={contextValue}>{children}</BatchProvider>;
}

function getTagFilter({
  tagFilter,
  entity,
  fieldOverrides,
}: {
  tagFilter?: API.FilterByComponentFragment;
  entity: API.CreateWorkflowEntity;
  fieldOverrides: BatchFieldOverrides;
}): API.FilterByComponentFragment | undefined {
  if (isNil(tagFilter)) {
    return undefined;
  }

  // Return early if the tagFilter already has a meaningful value set (containsString)
  const hasExistingValues = (): boolean => {
    const value = tagFilter?.value;
    if (!value) {
      return false;
    }
    // "containsString" is considered set if it has at least one value
    if (
      value.containsString &&
      Array.isArray(value.containsString.values) &&
      value.containsString.values.length > 0
    ) {
      return true;
    }
    return false;
  };

  if (hasExistingValues()) {
    return tagFilter;
  }

  const componentId = tagFilter?.componentId;

  // Attempt to derive tag values from upcomingEntityFieldOverrides first
  let derivedTagValues = fieldOverrides?.upcomingEntityFieldOverrides?.fields?.[componentId];

  // Fall back to the entity field value if no overrides are present
  if (!derivedTagValues || derivedTagValues.length === 0) {
    const entityField = entity?.fields?.[componentId];

    if (Array.isArray(entityField)) {
      derivedTagValues = entityField;
    } else if (entityField && Array.isArray(entityField.tagIds)) {
      // Handle TagField class instance
      derivedTagValues = entityField.tagIds;
    }
  }

  // Build the new FilterByComponent value
  const newFilterValue: API.FilterOneOf = {
    containsString: null,
    isEmpty: null,
    stringWildcard: null,
    containsNumber: null,
    dateIs: null,
    dateRange: null,
    dateTokenIs: null,
    numberIs: null,
    containsCurrentUser: null,
  };

  if (Array.isArray(derivedTagValues)) {
    newFilterValue.containsString = {
      values: derivedTagValues,
      negate: false,
    };
  } else {
    return undefined;
  }

  return {
    componentId,
    value: newFilterValue,
  };
}

// seriesFilter values cannot match form values that will be applied to upcoming entities
function validateSeriesFilter({
  seriesFilter,
  entity,
  fieldOverrides,
}: {
  seriesFilter?: API.FilterByComponentFragment;
  entity: API.CreateWorkflowEntity;
  fieldOverrides: BatchFieldOverrides;
}): boolean {
  // If there is no filter or it has no meaningful values, it is considered valid.
  const containsString = seriesFilter?.value?.containsString;
  const filterValues = containsString?.values ?? [];
  const negate = containsString?.negate ?? false;

  if (isNil(filterValues) || isEmpty(filterValues)) {
    return true;
  }

  const componentId = seriesFilter?.componentId;

  if (isNil(componentId)) {
    return true;
  }

  // Helper to determine validity based on overlap & negate flag
  // do we want to match the form's field values or not?
  const isValidOverlap = (overlap: boolean): boolean => {
    const result = negate ? !overlap : overlap;

    return result;
  };

  // 1. Prefer the upcomingEntityFieldOverrides (these are applied to upcoming entities).
  const upcomingFields = fieldOverrides?.upcomingEntityFieldOverrides?.fields ?? {};
  const upcomingTagValues = upcomingFields[componentId] ?? [];

  if (Array.isArray(upcomingTagValues) && upcomingTagValues.length > 0) {
    const overlap = upcomingTagValues.some(tag => filterValues.includes(tag));

    return isValidOverlap(overlap);
  }

  // 2. Fall back to the original entity field values if no overrides exist.
  const entityField = entity?.fields?.[componentId];

  let entityTagValues: string[] = [];

  if (Array.isArray(entityField)) {
    entityTagValues = entityField;
  } else if (entityField && Array.isArray(entityField.tagIds)) {
    entityTagValues = entityField.tagIds;
  }

  if (entityTagValues.length > 0) {
    const overlap = entityTagValues.some(tag => filterValues.includes(tag));

    return isValidOverlap(overlap);
  }

  // If we have no information to compare against, default to valid.
  return true;
}
